import styled, { css } from "styled-components";

import FallbackImage from "components/global/FallbackImage";

const fieldChrome = css`
  width: 100%;
  border-radius: 1rem;
  border: 1px solid var(--brand-border);
  background: rgba(255, 255, 255, 0.04);
  color: var(--brand-ink);
  font-size: 0.95rem;
  line-height: 1.5;
  padding: 0.85rem 1rem;
  transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;

  &:focus {
    border-color: var(--brand-highlight);
    box-shadow: var(--brand-input-focus-ring);
    outline: none;
  }

  &[aria-invalid="true"] {
    border-color: var(--brand-error-border);
    box-shadow: 0 0 0 3px var(--brand-error-ring);
  }

  &:disabled {
    background: rgba(255, 255, 255, 0.05);
    color: var(--brand-muted);
    cursor: not-allowed;
  }
`;

const selectMenuChrome = css`
  color-scheme: dark;

  option,
  optgroup {
    background: #151b23;
    color: var(--brand-ink);
  }

  option:disabled {
    color: var(--brand-muted);
  }
`;

const actionButtonChrome = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  border-radius: 1rem;
  border: 1px solid transparent;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.85rem 1.1rem;
  transition: transform 160ms ease, border-color 160ms ease, background 160ms ease, color 160ms ease,
    box-shadow 160ms ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
    transform: none;
  }
`;

export const FormStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const FormGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const WideField = styled.div`
  @media (min-width: 768px) {
    grid-column: span 2 / span 2;
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FieldHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: flex-start;

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const FieldHeaderCopy = styled.div`
  align-self: stretch;
`;

export const FieldHeaderAction = styled.div`
  align-self: stretch;

  @media (min-width: 640px) {
    align-self: flex-start;
    width: auto;
  }
`;

export const FieldLabel = styled.label`
  color: var(--brand-muted);
  font-size: 0.8125rem;
`;

export const FieldHelper = styled.p`
  color: var(--brand-muted);
  font-size: 0.75rem;
  line-height: 1.4rem;
`;

export const ValidationErrorText = styled.p`
  color: var(--brand-error-text);
  font-size: 0.75rem;
`;

export const TextInput = styled.input`
  ${fieldChrome}
`;

export const SelectInput = styled.select`
  ${fieldChrome}
  ${selectMenuChrome}
`;

export const TextareaInput = styled.textarea`
  ${fieldChrome}
  min-height: 7rem;
  resize: vertical;
`;

export const CollectionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CollectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  @media (min-width: 640px) {
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
  }
`;

export const CollectionRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const CollectionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

export const CollectionRowSurface = styled(CollectionRow)`
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--brand-border);
  border-radius: 1.25rem;
  padding: 0.6rem;
`;

export const CollectionGrid = styled.div`
  display: grid;
  gap: 0.75rem;
  width: 100%;
`;

export const MetadataCollectionGrid = styled(CollectionGrid)`
  grid-template-columns: minmax(0, 1fr);

  @media (min-width: 480px) {
    grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.1fr);
  }
`;

export const ArrayCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--brand-border);
  border-radius: 1.5rem;
  box-shadow: 0 16px 24px rgba(0, 0, 0, 0.18);
  padding: 1rem;
`;

export const ArrayCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: space-between;
`;

export const ArrayCardTitle = styled.p`
  color: var(--brand-ink);
  font-size: 0.875rem;
  font-weight: 600;
`;

export const ArrayCardBody = styled.div`
  display: grid;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const SecondaryButton = styled.button`
  ${actionButtonChrome}
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--brand-border);
  color: var(--brand-ink);

  &:hover:not(:disabled) {
    border-color: var(--brand-highlight);
    background: rgba(240, 191, 86, 0.08);
  }
`;

export const PrimaryButton = styled.button`
  ${actionButtonChrome}
  background: var(--brand-highlight);
  color: #1b1306;

  &:hover:not(:disabled) {
    background: var(--brand-accent-deep);
    color: white;
  }
`;

export const DangerIconButton = styled.button`
  ${actionButtonChrome}
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border-color: rgba(244, 63, 94, 0.2);
  color: #fecaca;
  background: rgba(220, 38, 38, 0.08);

  &:hover:not(:disabled) {
    background: rgba(220, 38, 38, 0.16);
  }
`;

export const UploadButton = styled.label`
  ${actionButtonChrome}
  background: rgba(255, 255, 255, 0.04);
  border-color: var(--brand-border);
  color: var(--brand-ink);
  cursor: pointer;
  justify-content: center;
  width: 100%;

  &:hover {
    border-color: var(--brand-highlight);
  }

  @media (min-width: 640px) {
    width: fit-content;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const ImageSurface = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border-radius: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.875rem;

  @media (min-width: 640px) {
    padding: 1rem;
  }
`;

export const ImageFieldRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  text-align: center;

  @media (min-width: 640px) {
    align-items: flex-start;
    flex-direction: row;
    text-align: left;
  }
`;

export const ImageControlStack = styled.div`
  flex: 1;
  min-width: 0;
  width: 100%;
`;

export const PreviewImage = styled(FallbackImage)`
  height: 6.5rem;
  width: 6.5rem;
  flex-shrink: 0;
  border-radius: 1.25rem;

  @media (min-width: 640px) {
    height: 7.5rem;
    width: 7.5rem;
  }
`;

export const ImagePlaceholder = styled.div`
  align-items: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px dashed var(--brand-border);
  border-radius: 1.25rem;
  color: var(--brand-muted);
  display: flex;
  font-size: 0.8125rem;
  height: 6.5rem;
  justify-content: center;
  width: 6.5rem;

  @media (min-width: 640px) {
    height: 7.5rem;
    width: 7.5rem;
  }
`;

export const ImagePlaceholderLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

export const SelectedFileSurface = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--brand-border);
  border-radius: 1.25rem;
  color: var(--brand-muted);
  font-size: 0.875rem;
  overflow-wrap: anywhere;
  padding: 0.75rem 1rem;
`;

export const CheckboxCard = styled.label`
  align-items: center;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--brand-border);
  border-radius: 1rem;
  color: var(--brand-ink);
  display: flex;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
`;

export const CheckboxLabel = styled.span`
  font-size: 0.8125rem;
`;

export const ActionRow = styled.div`
  border-top: 1px solid var(--brand-border);
  display: flex;
  flex-direction: column-reverse;
  gap: 0.75rem;
  padding-top: 1.25rem;

  & > * {
    width: 100%;
  }

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: flex-end;

    & > * {
      width: auto;
    }
  }
`;
